/**
 * The sales training progress: TanStack Query hooks over `/training/*`.
 *
 * The server keeps where the signed-in person stands (`GET /training/progress`) and
 * decides what a write may change; the rules are mirrored here for two reasons:
 *   - optimistic updates: the page moves on before the server answers;
 *   - the offline fallback: when the app DB is not available (503) or the network is
 *     down (status 0), progress is kept in this browser (`i2q.training.v1.progress`) and
 *     the page says so. Nothing else is stored in the browser.
 *
 * The rules (same as `backend/src/rfq_foundry/training.py`): a module goes from started
 * to completed and never back; a scored module (`quiz`, `quick-start`) is completed only
 * by a passing attempt; a pass is `correct * 100 >= 80 * total`; only a passing score is
 * kept; the course is completed only after a passed quiz.
 *
 * Every request carries the session cookie only: no route takes a user id.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/logger";

import type { ApiError } from "./client";
import {
  getTrainingProgress,
  getTrainingTeam,
  putTrainingModule,
  putTrainingState,
  recordTrainingAttempt,
  resetTraining,
  type TrainingModuleProgress,
  type TrainingProgress,
  type TrainingQuizResult,
  type TrainingTeam,
} from "./generated";

export type {
  TrainingModuleProgress,
  TrainingNotStarted,
  TrainingProgress,
  TrainingQuizResult,
  TrainingTeam,
  TrainingTeamMember,
} from "./generated";

export const TRAINING_COURSE = "i2q-sales" as const;
export const TRAINING_MASTERY_PERCENT = 80;
export const TRAINING_SCORED_MODULES: readonly string[] = ["quiz", "quick-start"];
export const TRAINING_FINAL_QUIZ = "quiz";
export const TRAINING_QUICK_START = "quick-start";
/** Modules that are not lessons (the lesson count of the overview skips them). */
const NOT_LESSONS: readonly string[] = ["quiz", "quick-start", "closing"];

export const trainingProgressKey = ["training-progress", TRAINING_COURSE] as const;
export const trainingTeamKey = (area: string | null) =>
  ["training-team", TRAINING_COURSE, area ?? "all"] as const;

/** Where the progress is mirrored when the server cannot keep it. */
export const LOCAL_PROGRESS_KEY = "i2q.training.v1.progress";
/** The first-login note was dismissed in this browser (the server was not available). */
export const LOCAL_NUDGE_OFF_KEY = "i2q.training.v1.nudge.off";

/** Shown while progress lives in this browser only. */
export const OFFLINE_PROGRESS_TEXT = "Your progress is saved in this browser only for now.";

/** The progress the page reads: the server's, or the browser copy with `offline`. */
export type TrainingProgressView = Omit<TrainingProgress, "modules"> & {
  modules: TrainingModuleProgress[];
  offline?: boolean;
};

// ─── the rules, mirrored ─────────────────────────────────────────────────────

/** Whether `correct` of `total` reaches 80 % (integer arithmetic, as the server). */
export function trainingPassed(correct: number, total: number): boolean {
  if (total <= 0) {
    return false;
  }
  return correct * 100 >= TRAINING_MASTERY_PERCENT * total;
}

/** The score in whole percent, rounded down (10 of 12 is 83). */
export function trainingScore(correct: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.floor((correct * 100) / total);
}

/** Progress before the first write. */
export function emptyProgress(): TrainingProgressView {
  return {
    course_id: TRAINING_COURSE,
    mastery_percent: TRAINING_MASTERY_PERCENT,
    content_version: 1,
    modules: [],
    resume_module_id: null,
    resume_slide: 0,
    quick_start_done_at: null,
    nudge_dismissed_at: null,
    completed_at: null,
    ever_completed_at: null,
    final_score: null,
  };
}

export interface ModuleWrite {
  moduleId: string;
  status: "started" | "completed";
  slideIndex: number;
  contentVersion: number;
}

export interface AttemptWrite {
  moduleId: "quiz" | "quick-start";
  correct: number;
  total: number;
  contentVersion: number;
}

export interface StateWrite {
  resumeModuleId?: string | null;
  resumeSlide?: number | null;
  nudgeDismissed?: boolean | null;
  courseCompleted?: boolean | null;
}

const nowIso = () => new Date().toISOString();

/** The progress after a module write (monotonic; a scored module is never completed here). */
export function applyModuleWrite(
  progress: TrainingProgressView,
  write: ModuleWrite,
  at: string = nowIso(),
): TrainingProgressView {
  const current = progress.modules.find((m) => m.module_id === write.moduleId);
  const scored = TRAINING_SCORED_MODULES.includes(write.moduleId);
  const wantsDone = write.status === "completed" && !scored;
  const slide = Math.max(0, Math.min(50, write.slideIndex));
  const next: TrainingModuleProgress = current
    ? {
        ...current,
        status: current.status === "completed" || wantsDone ? "completed" : "started",
        completed_at: current.completed_at ?? (wantsDone ? at : null),
        slide_index: slide,
        updated_at: at,
      }
    : {
        module_id: write.moduleId,
        status: wantsDone ? "completed" : "started",
        slide_index: slide,
        best_score: null,
        attempts: 0,
        started_at: at,
        completed_at: wantsDone ? at : null,
        updated_at: at,
      };
  return {
    ...progress,
    modules: current
      ? progress.modules.map((m) => (m.module_id === write.moduleId ? next : m))
      : [...progress.modules, next],
    resume_module_id: write.moduleId,
    resume_slide: slide,
  };
}

/** The progress and the result after one attempt (only a pass keeps a score). */
export function applyAttemptWrite(
  progress: TrainingProgressView,
  write: AttemptWrite,
  at: string = nowIso(),
): { progress: TrainingProgressView; result: TrainingQuizResult } {
  const ok = trainingPassed(write.correct, write.total);
  const pct = trainingScore(write.correct, write.total);
  const current = progress.modules.find((m) => m.module_id === write.moduleId);
  const base: TrainingModuleProgress = current ?? {
    module_id: write.moduleId,
    status: "started",
    slide_index: 0,
    best_score: null,
    attempts: 0,
    started_at: at,
    completed_at: null,
    updated_at: at,
  };
  const best = ok ? Math.max(base.best_score ?? 0, pct) : (base.best_score ?? null);
  const row: TrainingModuleProgress = {
    ...base,
    attempts: (base.attempts ?? 0) + 1,
    status: ok ? "completed" : base.status,
    best_score: best,
    completed_at: ok ? (base.completed_at ?? at) : (base.completed_at ?? null),
    updated_at: at,
  };
  const modules = current
    ? progress.modules.map((m) => (m.module_id === write.moduleId ? row : m))
    : [...progress.modules, row];
  const quickStart =
    ok && write.moduleId === TRAINING_QUICK_START && !progress.quick_start_done_at
      ? at
      : (progress.quick_start_done_at ?? null);
  return {
    progress: { ...progress, modules, quick_start_done_at: quickStart },
    result: {
      passed: ok,
      score: pct,
      best_score: row.best_score ?? null,
      attempts: row.attempts ?? 1,
      module: row,
    },
  };
}

/** Whether the course may be completed: a passed final quiz. */
export function canCompleteCourse(progress: TrainingProgressView): boolean {
  const quiz = progress.modules.find((m) => m.module_id === TRAINING_FINAL_QUIZ);
  return quiz?.status === "completed" && (quiz.best_score ?? -1) >= TRAINING_MASTERY_PERCENT;
}

/** Raised by the local mirror like the server's 409. */
export const PASS_FIRST_TEXT = "Pass the knowledge check first.";

/** The progress after a state write; throws `{status: 409}` like the server. */
export function applyStateWrite(
  progress: TrainingProgressView,
  write: StateWrite,
  at: string = nowIso(),
): TrainingProgressView {
  const next: TrainingProgressView = { ...progress };
  if (write.resumeModuleId != null) {
    next.resume_module_id = write.resumeModuleId;
  }
  if (write.resumeSlide != null) {
    next.resume_slide = write.resumeSlide;
  }
  if (write.nudgeDismissed === true) {
    next.nudge_dismissed_at = progress.nudge_dismissed_at ?? at;
  } else if (write.nudgeDismissed === false) {
    next.nudge_dismissed_at = null;
  }
  if (write.courseCompleted) {
    if (!canCompleteCourse(progress)) {
      throw { status: 409, body: { detail: PASS_FIRST_TEXT } } satisfies ApiError;
    }
    const quiz = progress.modules.find((m) => m.module_id === TRAINING_FINAL_QUIZ);
    next.completed_at = progress.completed_at ?? at;
    next.ever_completed_at = progress.ever_completed_at ?? at;
    next.final_score = quiz?.best_score ?? null;
  }
  return next;
}

/** The progress after "Restart training": the first completion stays on record. */
export function applyReset(progress: TrainingProgressView): TrainingProgressView {
  return {
    ...emptyProgress(),
    ever_completed_at: progress.ever_completed_at ?? null,
    final_score: progress.final_score ?? null,
    offline: progress.offline,
  };
}

// ─── the browser copy ────────────────────────────────────────────────────────

/** The browser copy of the progress, or null (none, or no storage). */
export function readLocalProgress(): TrainingProgressView | null {
  try {
    const raw = window.localStorage.getItem(LOCAL_PROGRESS_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as TrainingProgressView;
    return parsed && Array.isArray(parsed.modules) ? parsed : null;
  } catch {
    return null;
  }
}

/** Keep the progress in this browser. Fails silently without storage. */
export function writeLocalProgress(progress: TrainingProgressView): void {
  try {
    window.localStorage.setItem(LOCAL_PROGRESS_KEY, JSON.stringify(progress));
  } catch {
    // No storage: the progress lasts for this page only.
  }
}

/** Whether a failed request means "keep it in the browser" (503 or no response). */
export function isTrainingOffline(error: unknown): boolean {
  const status = (error as Partial<ApiError> | null)?.status;
  return status === 503 || status === 0;
}

// ─── requests ────────────────────────────────────────────────────────────────

async function fetchProgress(): Promise<TrainingProgressView> {
  try {
    const { data } = await getTrainingProgress({
      query: { course: TRAINING_COURSE },
      throwOnError: true,
    });
    const progress = data as TrainingProgress;
    return { ...progress, modules: progress.modules ?? [] };
  } catch (error) {
    if (isTrainingOffline(error)) {
      logger.warn("[training] progress kept in this browser", error);
      return { ...(readLocalProgress() ?? emptyProgress()), offline: true };
    }
    throw error;
  }
}

function cached(queryClient: ReturnType<typeof useQueryClient>): TrainingProgressView {
  return (
    queryClient.getQueryData<TrainingProgressView>(trainingProgressKey) ??
    readLocalProgress() ??
    emptyProgress()
  );
}

function keepLocal(progress: TrainingProgressView): TrainingProgressView {
  const offline = { ...progress, offline: true };
  writeLocalProgress(offline);
  return offline;
}

/** The signed-in person's progress. A 503 or no network falls back to the browser copy. */
export function useTrainingProgress(enabled = true) {
  return useQuery({
    queryKey: trainingProgressKey,
    queryFn: fetchProgress,
    enabled,
    retry: (count, error) => !isTrainingOffline(error) && count < 1,
  });
}

/** The optimistic-update scaffolding every progress write shares. */
function useProgressMutation<Args, Result>(
  send: (args: Args) => Promise<Result>,
  local: (progress: TrainingProgressView, args: Args) => TrainingProgressView,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: Args) => {
      const before = cached(queryClient);
      if (before.offline) {
        keepLocal(local(before, args));
        return null;
      }
      try {
        return await send(args);
      } catch (error) {
        if (isTrainingOffline(error)) {
          keepLocal(local(before, args));
          return null;
        }
        throw error;
      }
    },
    onMutate: async (args: Args) => {
      await queryClient.cancelQueries({ queryKey: trainingProgressKey });
      const previous = queryClient.getQueryData<TrainingProgressView>(trainingProgressKey);
      if (previous) {
        try {
          queryClient.setQueryData(trainingProgressKey, local(previous, args));
        } catch {
          // The rule refused it (e.g. completion before a pass): the server answers.
        }
      }
      return { previous };
    },
    onError: (_error, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(trainingProgressKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trainingProgressKey });
    },
  });
}

/** Start a module, move to a slide or complete a lesson. */
export function usePutTrainingModule() {
  return useProgressMutation<ModuleWrite, unknown>(
    async (write) => {
      const { data } = await putTrainingModule({
        path: { module_id: write.moduleId },
        body: {
          course_id: TRAINING_COURSE,
          status: write.status,
          slide_index: write.slideIndex,
          content_version: write.contentVersion,
        },
        throwOnError: true,
      });
      return data;
    },
    (progress, write) => applyModuleWrite(progress, write),
  );
}

/** Record one knowledge-check attempt; answers with the result (also offline). */
export function useRecordTrainingAttempt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (write: AttemptWrite): Promise<TrainingQuizResult> => {
      const before = cached(queryClient);
      const offlineResult = () => {
        const out = applyAttemptWrite(before, write);
        keepLocal(out.progress);
        return out.result;
      };
      if (before.offline) {
        return offlineResult();
      }
      try {
        const { data } = await recordTrainingAttempt({
          body: {
            course_id: TRAINING_COURSE,
            module_id: write.moduleId,
            correct: write.correct,
            total: write.total,
            content_version: write.contentVersion,
          },
          throwOnError: true,
        });
        return data as TrainingQuizResult;
      } catch (error) {
        if (isTrainingOffline(error)) {
          return offlineResult();
        }
        throw error;
      }
    },
    onSuccess: (_result, write) => {
      queryClient.setQueryData<TrainingProgressView>(trainingProgressKey, (old) =>
        old ? applyAttemptWrite(old, write).progress : old,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trainingProgressKey });
    },
  });
}

/** Resume point, the first-login note, or the course completion (409 before a quiz pass). */
export function usePutTrainingState() {
  return useProgressMutation<StateWrite, unknown>(
    async (write) => {
      const { data } = await putTrainingState({
        body: {
          course_id: TRAINING_COURSE,
          resume_module_id: write.resumeModuleId ?? null,
          resume_slide: write.resumeSlide ?? null,
          nudge_dismissed: write.nudgeDismissed ?? null,
          course_completed: write.courseCompleted ?? null,
        },
        throwOnError: true,
      });
      return data;
    },
    (progress, write) => applyStateWrite(progress, write),
  );
}

/** "Restart training": modules cleared, the first completion kept. */
export function useResetTraining() {
  return useProgressMutation<void, unknown>(
    async () => {
      const { data } = await resetTraining({
        body: { course_id: TRAINING_COURSE },
        throwOnError: true,
      });
      return data;
    },
    (progress) => applyReset(progress),
  );
}

/** Training uptake of the caller's areas (admins, `see_team`; the server is the gate). */
export function useTrainingTeam(area: string | null, enabled = true) {
  return useQuery({
    queryKey: trainingTeamKey(area),
    queryFn: async (): Promise<TrainingTeam> => {
      const { data } = await getTrainingTeam({
        query: { course: TRAINING_COURSE, ...(area ? { area } : {}) },
        throwOnError: true,
      });
      return data as TrainingTeam;
    },
    enabled,
    retry: false,
  });
}

/** Whether this browser dismissed the first-login note (server not available). */
export function localNudgeOff(): boolean {
  try {
    return window.localStorage.getItem(LOCAL_NUDGE_OFF_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remember in this browser that the first-login note was dismissed. */
export function setLocalNudgeOff(): void {
  try {
    window.localStorage.setItem(LOCAL_NUDGE_OFF_KEY, "1");
  } catch {
    // No storage: the note comes back on the next visit.
  }
}

export interface TrainingSummary {
  /** Progress has loaded (from the server or the browser copy). */
  loaded: boolean;
  quickStartDone: boolean;
  completed: boolean;
  /** Completed lesson modules (not the quick start, the quiz or the closing). */
  lessonsDone: number;
  /** The first-login note on the queue. */
  showNudge: boolean;
  /** The "New" badge next to Training in the menu. */
  showNewBadge: boolean;
}

/** What the menu badge and the first-login note read, from a progress value. */
export function trainingSummaryOf(
  progress: TrainingProgressView | undefined,
  nudgeOffInBrowser = false,
): TrainingSummary {
  if (!progress) {
    return {
      loaded: false,
      quickStartDone: false,
      completed: false,
      lessonsDone: 0,
      showNudge: false,
      showNewBadge: false,
    };
  }
  const quickStartDone = Boolean(progress.quick_start_done_at);
  const completed = Boolean(progress.completed_at);
  const lessonsDone = progress.modules.filter(
    (m) => m.status === "completed" && !NOT_LESSONS.includes(m.module_id),
  ).length;
  const dismissed = Boolean(progress.nudge_dismissed_at) || nudgeOffInBrowser;
  return {
    loaded: true,
    quickStartDone,
    completed,
    lessonsDone,
    showNudge: !quickStartDone && !completed && !dismissed,
    showNewBadge: !completed,
  };
}

/** The derived summary of the signed-in person's training. */
export function useTrainingSummary(enabled = true): TrainingSummary {
  const { data } = useTrainingProgress(enabled);
  return trainingSummaryOf(data, data?.offline ? localNudgeOff() : false);
}
